import { message, Modal } from 'antd'

import {
    InteractionOutlined,
    LikeOutlined,
    CloseCircleOutlined,
    ImportOutlined,
    PhoneFilled,
    DesktopOutlined,
    UserSwitchOutlined,
    DeleteOutlined,
} from '@ant-design/icons'

import { useAudioCallContext } from '../../context/AudioCallProvider'
import { useCaseViewContext } from '../../context/CaseViewContext'

import {
    CaseStatusEnum,
    CASE_TAGS,
    REQUEST_STATUS,
    STATUS_TYPE
} from '../../constants'

import { StyledButton } from '../Button'

export const ActionButtons = ({
    requestIsCompleted,
    approveReview,
    showReviewModal,
    setShowRxChangeModal,
    setShowReassignModal,
    setShowUpdateCaseStatusModal,
}) => {

    const {
        caseDetails,
        fetchCaseDetails,
        apiClasses,
        apiType,
        readOnlyMode,
        isReviewerProvider,
        selfAssignCase,
        isLeadProvider,
        activeRequest,
    } = useCaseViewContext()

    const audioCall = useAudioCallContext()

    const caseIsCompleted = caseDetails?.status === CaseStatusEnum.CONSULT_COMPLETE || caseDetails?.status === CaseStatusEnum.CONSULT_COMPLETE_NO_RX
    const caseNeedsMoreInfo = [CaseStatusEnum.ADDITIONAL_INFO_REQUIRED, CaseStatusEnum.INVALID_FACE_PHOTO, CaseStatusEnum.INVALID_PHOTO_ID].includes(caseDetails?.status)
    const caseIsReviewed = caseDetails?.status === CaseStatusEnum.REFER && caseDetails?.tags?.includes(CASE_TAGS.REVIEWED)
    const canRequestRxChange = apiType === 'admin' && (caseIsCompleted || caseNeedsMoreInfo) && caseDetails?.status !== CaseStatusEnum.RX_CHANGE
    const canTakeOverCase = readOnlyMode && isLeadProvider && !caseIsReviewed

    const onClickPhone = () => audioCall.initiate(caseDetails?._id)

    const onClickVideo = () => {
        const meetingLink = caseDetails?.consultation?.meeting?.link
        if (!meetingLink) {
            return message.warning("No video consultation scheduled or no meeting link configured. Please contact your administrator.")
        }

        window.open(meetingLink, '_blank', 'location=yes,scrollbars=yes,status=yes')
    }

    const handleMissedConsult = async () => {
        const isAdmin = apiType === 'admin'
        const hasHandleMissedConsultationMethod = isAdmin ?
            apiClasses?.ConsultationAPI.handleMissedConsultation :
            apiClasses?.PhysicianAPI.handleMissedConsultation;

        if (!hasHandleMissedConsultationMethod) {
            message.error("PhysicianAPI or ConsultationAPI is not available");
            return;
        }

        const caseId = isAdmin ? caseDetails?.caseId : caseDetails?._id;

        if (!caseId) {
            message.error("Case ID is not available");
            return;
        }

        try {

            const response = isAdmin ?
                await apiClasses?.ConsultationAPI.handleMissedConsultation(caseId) :
                await apiClasses?.PhysicianAPI.handleMissedConsultation(caseId);

            if (response.status !== 200) {
                message.error("Failed to handle missed consultation");
                return;
            }

            message.success("Case noted as missed")
            fetchCaseDetails(caseId)
        } catch (e) {
            message.error('Error handling missed consultation')
            console.error("Error handling missed consultation:", e)
        }
    }

    const handleCancelRequest = async () => {
        if (apiClasses?.CasesApi) {
            const caseId = caseDetails?._id || caseDetails?.caseId
            try {
                await apiClasses.CasesApi.updateCaseStatusV2(caseId, {
                    type: STATUS_TYPE.REQUEST,
                    status: REQUEST_STATUS.CANCELED,
                })
            } catch (error) {
                message.error("Failed to cancel request")
            } finally {
                fetchCaseDetails()
            }
        }
    }

    return (
        <div style={{ display: 'flex', gap: '5px', margin: '5px 0' }}>
            {
                canRequestRxChange && (
                    <StyledButton
                        id="case-action-change-rx-btn"
                        Icon={InteractionOutlined}
                        tooltip="Change Rx"
                        color="orange"
                        size="small"
                        onClick={() => setShowRxChangeModal(true)}
                    />
                )
            }
            {
                apiType === 'admin' &&
                (

                    <StyledButton
                        id="case-action-reassign-btn"
                        Icon={UserSwitchOutlined}
                        tooltip="Reassign"
                        color="#7BAD7E"
                        size="small"
                        onClick={() => setShowReassignModal(true)}
                    />

                )
            }
            {
                /* 
                Don't allow providers to update status after the request is completed 
                If they need to prescribe additional medications they can do so even if the request is completed
                */
                (apiType === 'admin' || !requestIsCompleted) && (
                    <>
                        <StyledButton
                            id="case-action-update-status-btn"
                            Icon={InteractionOutlined}
                            tooltip="Update Status"
                            color="rgba(204, 125, 203)"
                            size="small"
                            onClick={() => setShowUpdateCaseStatusModal(true)}
                        />
                        {
                            ![CaseStatusEnum.CONSULT_COMPLETE, CaseStatusEnum.CONSULT_COMPLETE_NO_RX].includes(caseDetails?.status) && apiType === 'admin' && (
                                <StyledButton
                                    id="case-action-cancel-request-btn"
                                    Icon={DeleteOutlined}
                                    tooltip="Cancel Request"
                                    color="red"
                                    size="small"
                                    onClick={handleCancelRequest}
                                />
                            )
                        }
                    </>
                )
            }
            {
                !readOnlyMode && (
                    <StyledButton
                        id="case-action-call-patient-btn"
                        size='small'
                        Icon={PhoneFilled}
                        tooltip="Call the patient"
                        onClick={onClickPhone}
                    />
                )
            }
            {
                !readOnlyMode && [CaseStatusEnum.PENDING_VIDEO_CONSULT].includes(caseDetails?.status) && (
                    <StyledButton
                        id="case-action-video-consult-btn"
                        size='small'
                        Icon={DesktopOutlined}
                        tooltip="Begin video consultation"
                        onClick={onClickVideo}
                    />
                )
            }
            {
                [CaseStatusEnum.PENDING_AUDIO_CONSULT, CaseStatusEnum.PENDING_VIDEO_CONSULT].includes(caseDetails?.status) &&
                <StyledButton
                    id="case-action-missed-consult-btn"
                    size='small'
                    content={<p style={{ margin: 0 }}>&#128532;</p>}
                    color="orange"
                    tooltip="Patient missed consultation"
                    onClick={handleMissedConsult}
                />
            }
            {
                caseDetails?.status === CaseStatusEnum.REFER &&
                isReviewerProvider &&
                !caseIsReviewed && (
                    <StyledButton
                        id="case-action-confirm-rejection-btn"
                        size='small'
                        Icon={LikeOutlined}
                        tooltip="Confirm Rejection"
                        onClick={approveReview}
                    />
                )
            }
            {
                caseDetails?.status === CaseStatusEnum.REFER &&
                isReviewerProvider &&
                !caseIsReviewed && (
                    <StyledButton
                        id="case-action-return-to-provider-btn"
                        size='small'
                        color="red"
                        Icon={CloseCircleOutlined}
                        tooltip="Return to Provider"
                        onClick={showReviewModal}
                    />
                )
            }
            {
                canTakeOverCase && (
                    <StyledButton
                        id="case-action-take-over-case-btn"
                        size='small'
                        Icon={ImportOutlined}
                        tooltip="Take over case"
                        color="blue"
                        onClick={() => Modal.confirm({
                            title: 'Take Over Case',
                            content: 'Would you like to take over this case?',
                            onOk: selfAssignCase
                        })}
                    />
                )
            }
        </div>
    )
}

export default ActionButtons